import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  TooltipProps,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Card/Card";
import { useTheme } from "@/context/ThemeContext";
import { LifetimeValueByTierData } from "@/modules/stochastic/features/DashboardPage/api/fetchStochasticDashboardData/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Info } from "lucide-react";
import { formatCurrency } from "@/modules/stochastic/features/DashboardPage/utils/chart";

type ViewMode = "households" | "sales";

interface CustomTooltipProps extends TooltipProps<ValueType, NameType> {
  viewMode: ViewMode;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  viewMode,
}) => {
  const { theme } = useTheme();

  if (!active || !payload || payload.length === 0) return null;

  const item = payload[0];
  const value = item?.value;

  return (
    <div
      className="p-2 text-sm rounded shadow"
      style={{
        backgroundColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
        border: `1px solid ${theme === "dark" ? "#374151" : "#E5E7EB"}`,
        color: theme === "dark" ? "#F3F4F6" : "#1F2937",
      }}
    >
      <div className="font-medium">
        <strong>Tier: {label ?? "N/A"}</strong>
      </div>
      <div style={{ color: "#3B82F6", fontWeight: 500 }}>
        <strong>
          {viewMode === "households" ? "Households: " : "Total Sales: "}
          {typeof value === "number"
            ? viewMode === "households"
              ? value.toLocaleString()
              : formatCurrency(value)
            : "N/A"}
        </strong>
      </div>
    </div>
  );
};

interface LifetimeValueByTierChartProps {
  initialData?: LifetimeValueByTierData;
}

export default function LifetimeValueByTierChart({
  initialData,
}: LifetimeValueByTierChartProps) {
  const { theme } = useTheme();
  const [viewMode, setViewMode] = useState<ViewMode>("households");

  const totalHouseholds = initialData?.totalHouseholdsCount;
  const chartData = initialData?.chartData ?? [];
  const totalSales = chartData.reduce((sum, item) => sum + item.totalSales, 0);

  const dataKey = viewMode === "households" ? "householdCount" : "totalSales";
  const legendName =
    viewMode === "households" ? "Household Count" : "Total Sales";

  return (
    <Card className="w-full bg-white dark:bg-secondary-dark">
      <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <CardTitle className="text-fontColor dark:text-light">
              Lifetime Value by Tier
            </CardTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-lg">
                  <p>
                    Identifies which customer segments contribute the most
                    long-term value to market share stability.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Customer lifetime value by spending level
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <ToggleGroup
            className="border rounded-lg"
            onValueChange={(value) => value && setViewMode(value as ViewMode)}
            type="single"
            value={viewMode}
          >
            <ToggleGroupItem
              aria-label="View households"
              className="px-3 py-1 text-sm data-[state=on]:bg-blue-500 data-[state=on]:text-white"
              value="households"
            >
              Households
            </ToggleGroupItem>
            <ToggleGroupItem
              aria-label="View sales"
              className="px-3 py-1 text-sm data-[state=on]:bg-blue-500 data-[state=on]:text-white"
              value="sales"
            >
              Total Sales
            </ToggleGroupItem>
          </ToggleGroup>
          {viewMode === "households" && typeof totalHouseholds === "number" && (
            <div className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-100">
              <b>{totalHouseholds.toLocaleString()} Households</b>
            </div>
          )}
          {viewMode === "sales" && totalSales > 0 && (
            <div className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-100">
              <b>{formatCurrency(totalSales)} Total</b>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer height={500} width="100%">
          <BarChart
            key={viewMode}
            data={chartData}
            margin={{ top: 5, right: 30, left: 45, bottom: 10 }}
          >
            <CartesianGrid
              stroke={theme === "dark" ? "#374151" : "#e5e7eb"}
              strokeDasharray="3 3"
            />
            <XAxis
              angle={-35}
              dataKey="tier"
              height={50}
              interval={0}
              stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
              textAnchor="end"
              tick={{ fontSize: 14 }}
            />
            <YAxis
              stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
              tickFormatter={(value) =>
                typeof value === "number"
                  ? viewMode === "households"
                    ? value.toLocaleString()
                    : formatCurrency(value)
                  : "N/A"
              }
            />
            <ChartTooltip content={<CustomTooltip viewMode={viewMode} />} />
            <Legend
              wrapperStyle={{
                paddingTop: "60px",
                textAlign: "center",
              }}
            />
            <Bar dataKey={dataKey} fill="#3B82F6" name={legendName} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
